/* eslint-disable react-hooks/exhaustive-deps */
import { useReducer, useMemo, memo, useEffect, useCallback } from 'react';
import GeneralContext from './GeneralContext';
import initialGeneralState from './initialGeneralState';
import generalReducer from 'contexts/general/reducer/general.reducer';
import * as action from 'contexts/general/reducer/general.actions';
import { useTranslation } from 'react-i18next';
import { ChildrenProps } from 'interfaces/general.interface';
import { useAuth0 } from '@auth0/auth0-react';
import {
  ICreatePointOfSaleCategoryAPIRequestBody,
  IGetPointsOfSaleCategoriesAPIResponseItem,
  IUpdatePointOfSaleCategoryAPIRequestBody,
} from 'api/endpoints/general/general.response.interface';
import * as GeneralTypes from './reducer/general.types';

function GeneralProvider(props: ChildrenProps) {
  const [general, dispatch] = useReducer(generalReducer, initialGeneralState);
  const { t: translate } = useTranslation();
  const { getAccessTokenSilently } = useAuth0();

  useEffect(() => {
    if (!general.ping) return;
    (async () => {
      const token = await getAccessTokenSilently();
      return await action.getExchangesAction({ dispatch, translate, token });
    })();
  }, [general.ping]);

  useEffect(() => {
    if (!general.ping) return;
    (async () => {
      const token = await getAccessTokenSilently();
      return await action.getCountriesAction({ dispatch, translate, token });
    })();
  }, [general.ping]);

  useEffect(() => {
    if (!general.ping) return;
    (async () => {
      const token = await getAccessTokenSilently();
      return await action.getAssociationStatesAction({ dispatch, translate, token });
    })();
  }, [general.ping]);

  useEffect(() => {
    if (!general.ping) return;
    (async () => {
      const token = await getAccessTokenSilently();
      return await action.getPointsOfSaleCategoriesAction({ dispatch, token });
    })();
  }, [general.ping]);

  const setPing = useCallback((value: boolean) => {
    dispatch({
      type: GeneralTypes.SET_PING,
      payload: value,
    });
  }, []);

  const selectCategory = useCallback(
    (category: IGetPointsOfSaleCategoriesAPIResponseItem | null) => {
      dispatch({
        type: GeneralTypes.SET_CATEGORY_SELECTED,
        payload: category,
      });
    },
    []
  );

  const createCategory = useCallback(
    async ({ body }: { body: ICreatePointOfSaleCategoryAPIRequestBody }) => {
      const token = await getAccessTokenSilently();
      const create = await action.createCategoryAction({
        dispatch,
        body,
        token,
      });
      return create;
    },
    [getAccessTokenSilently]
  );

  const updateCategory = useCallback(
    async ({ body }: { body: IUpdatePointOfSaleCategoryAPIRequestBody }) => {
      const token = await getAccessTokenSilently();
      const update = await action.updateCategoryAction({
        dispatch,
        body,
        token,
      });
      return update;
    },
    [getAccessTokenSilently]
  );

  const deleteCategory = useCallback(
    async ({ categoryId }: { categoryId: string }) => {
      const token = await getAccessTokenSilently();
      const deleteCategoryFetch = await action.deleteCategoryAction({
        dispatch,
        categoryId,
        token,
      });
      return deleteCategoryFetch;
    },
    [getAccessTokenSilently]
  );

  const memoProvider = useMemo(
    () => ({
      ...general,
      setPing,
      selectCategory,
      createCategory,
      updateCategory,
      deleteCategory,
    }),
    [general, setPing, selectCategory, createCategory, updateCategory, deleteCategory]
  );

  return (
    <GeneralContext.Provider value={memoProvider}>
      {props.children}
    </GeneralContext.Provider>
  );
}

export default memo(GeneralProvider);
